import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import clsx from 'clsx';

import { Pagination } from 'edcom-ui';
import AppLink, { pushAppRoute } from '../components/links/AppLink';
import AdminLinks from '../components/AdminLinks/AdminLinks';
import CategoryTree from '../edcom-ui/components/NewTaxonomy/CategoryTree';
import {
  ContextLinks,
  SearchError,
  SearchFilters,
  SearchFooter,
  SearchFootnote,
  SearchResults,
  SearchTitle,
} from '../components/search';
import Filters from '../edcom-ui/components/Filters/Filters';
import Head from '../components/head';
import SelectedContentFacet from '../components/search/SearchFilters/SelectedContentFacet';
import { getSearchPageLinkingData } from '../components/LinkingData/util';
import track, { trackGA } from '../services/track';

import { replaceParams } from '../services/content';
import { CollectionsProvider } from '../services/CollectionsContext';

import styles from './search.module.less';

// EDENG-1220 - Teacher Appreciation Week
// EDENG-2065
const TAWDates = {
  begin: new Date('5/1/2023'),
  end: new Date('5/15/2023'),
};

const TAWIDs = ['175730', '175731', '175732'];

let searchTrigger = null;

export default function Search({
  total = 0,
  pages = 0,
  urlPath,
  meta,
  filter = {},
  currentFilters = [],
  facets = [],
  results = [],
  suggestions = [],
  replaceUrl = false,
  error = false,
  selectedCategory = {},
  viableCategories = {},
  params = {},
  selectedFilters = [],
  viableGrades = {},
  viableFilters = [],
  viableContentTypes = {},
  hasSubjectFilters = false,
}) {
  const router = useRouter();
  const { asPath } = router;
  const [selected, setSelected] = useState([]);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [multiSelectTracked, setMultiSelectTracked] = useState(false);
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [follow, setFollow] = useState(true);

  // State used to communicate to our child components that a request to C-S is in progress.
  // Child components should then disable themselves until we tell them that the
  // request has been completed.
  const [contentRequestInProcess, setContentRequestInProcess] = useState(false);

  const handleSelected = selected => {
    // if no selected, make sure it's view-all
    if (selected.length === 0) {
      setShowOnlySelected(false);
    } else if (selected.length > 1 && !multiSelectTracked) {
      trackGA('Search', 'Multiple items selected');
      setMultiSelectTracked(true);
    }
    setSelected(selected);
  };

  // Update URL if search response returned a different url
  React.useEffect(() => {
    if (replaceUrl) {
      // Replace history to change URL without re-rendering results
      window.history.replaceState(
        { ...window.history.state, as: urlPath },
        null,
        urlPath
      );
    }
    handleSelected(selected);
    setShowOnlySelected(false);
  }, [replaceUrl, asPath]);

  // Track search
  React.useEffect(() => {
    if (error || replaceUrl) {
      return;
    }

    let logEvent = searchTrigger;
    searchTrigger = null;

    if (logEvent === 'pagination') {
      return;
    }
    if (!logEvent) {
      logEvent = 'pageview';
    }

    const data = {
      nextjs: true,
      num_results: total,
      didyoumean: !!suggestions.length,
      trigger: logEvent,
      q: params.q,
      page: params.page,
      sort: params.sort,
    };

    track('Search', data);
  }, [urlPath, total, suggestions]);

  // EDENG-3757: update follow state to tell Google Crawler not to crawl multi-grade search pages
  React.useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(viableGrades, 'attributes')) {
      const numberOfSelectedGrades = viableGrades.attributes.filter(
        g => g.selected
      ).length;
      setFollow(numberOfSelectedGrades <= 1);
    }
    else {
      setFollow(false);
    }
  }, [viableGrades]);

  const generatePaginatedUrl = page => {
    return replaceParams(urlPath, { page: page > 1 ? page : null });
  };

  const handlePagination = (e, page) => {
    e.preventDefault();
    const href =
      parseInt(page) === 1
        ? replaceParams(urlPath, { page: '' })
        : replaceParams(urlPath, { page: page });
    searchTrigger = 'pagination';
    setContentRequestInProcess(true);
    pushAppRoute(router, href).then(() => {
      setContentRequestInProcess(false);
    });
    if (rootRef.current != null) {
      rootRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /**
   * @param {?string} href
   * @returns void
   */
  const handleClickSearchLink = async (href, queryChip = false) => {
    if (href == null) return;

    href = queryChip
      ? replaceParams(href, { q: null })
      : replaceParams(href, { page: null });
    searchTrigger = 'filter';
    setContentRequestInProcess(true);
    await pushAppRoute(router, href);
    setContentRequestInProcess(false);
  };

  const handleClickSuggestion = async term => {
    const href = replaceParams(urlPath, { q: term });
    searchTrigger = 'didyoumean';
    setContentRequestInProcess(true);
    await pushAppRoute(router, href);
    setContentRequestInProcess(false);
  };

  const clearResourceType = urlPath => {
    const allResourceTypes = viableContentTypes.attributes.map(
      viableContentType => {
        return '/' + viableContentType.urlSlice;
      }
    );

    return urlPath.replace(
      RegExp(allResourceTypes.join('|').replaceAll('/', '\\/'), 'g'),
      ''
    );
  };

  const handleClearFilters = () => {
    // The url to search with (pathName + non-facet query params)
    let queryString = urlPath
      .replace(/^[^?]+[?]?/, '')
      // Split into k=v parts
      .split('&')
      // Only include known search params
      .filter(val => val.match(/^(page|sort|q)=/)) // only non-facet filters
      .join('&');

    // using regular expression to remove grade filter
    // because grade filter is not an url params
    let searchLink = urlPath
      .replace(/\?.*/, '')
      .replace(/\/grade([^(/)]*)/, '');

    // clear the resource type filter for the url
    searchLink = clearResourceType(searchLink);

    if (queryString.length) {
      searchLink += '?' + decodeURIComponent(queryString);
    }

    handleClickSearchLink(searchLink);
  };

  const handleClickFacet = async (e, facetHref) => {
    e.preventDefault();
    handleClickSearchLink(facetHref);
  };

  let resultShown = showOnlySelected ? selected : results;
  let pagesShown = showOnlySelected ? 1 : pages;

  // EDENG-1220 - Teacher Appreciation Week
  const date = new Date();
  const time = date.getTime();

  if (time >= TAWDates.begin && time < TAWDates.end) {
    resultShown.forEach(function(result, index) {
      if (
        resultShown[index]['type'] === 'workbook' &&
        TAWIDs.includes(resultShown[index]['id'])
      ) {
        resultShown[index]['premium'] = false;
      }
    });
  }

  return (
    <div>
      <Head
        description={meta.metaDescription}
        index={meta.index}
        path={urlPath}
        linkingData={getSearchPageLinkingData(resultShown)}
        follow={follow}
      >
        <title>{meta.metaTitle}</title>
        {params.page * 1 === 2 && (
          <link rel="prev" href={replaceParams(urlPath, { page: null })} />
        )}
        {params.page > 2 && (
          <link
            rel="prev"
            href={replaceParams(urlPath, { page: params.page - 1 })}
          />
        )}
        {params.page < pagesShown && (
          <link
            rel="next"
            href={replaceParams(urlPath, { page: params.page * 1 + 1 })}
          />
        )}
      </Head>
      <div id="unified-search-results" ref={rootRef}>
        <div className={styles.searchResults}>
          {(!hasSubjectFilters || selectedCategory.treeLevel > 0) && (
            <CategoryTree
              selectedCategory={selectedCategory}
              viableCategories={viableCategories}
              onClick={handleClickFacet}
              contentRequestInProcess={contentRequestInProcess}
            />
          )}
          <CollectionsProvider>
            <div className={styles.facetsWrapper}>
              {selected.length > 0 && (
                <SelectedContentFacet
                  selected={selected}
                  unSelect={handleSelected}
                  showOnlySelected={showOnlySelected}
                  setShowOnlySelected={setShowOnlySelected}
                />
              )}
              <Filters
                total={total}
                viableGrades={viableGrades}
                viableFilters={viableFilters}
                viableContentTypes={viableContentTypes}
                onClick={handleClickFacet}
                open={open}
                closeFilterWrap={() => setOpen(false)}
                clearFilters={handleClearFilters}
                onFilter={handleClickSearchLink}
                selectedFilters={selectedFilters}
                contentRequestInProcess={contentRequestInProcess}
                selectedCategory={selectedCategory.text}
                queries={params.q}
                urlPath={urlPath}
              />
            </div>
            {results.length > 0 && (
              <SearchTitle
                title={meta.title}
                contextualLinksTitle={meta.contextualLinksTitle || ''}
                contextualLinks={meta.contextualLinks || []}
              >
                {meta.contextualLinks && meta.contextualLinks.length > 0 && (
                  <ContextLinks title={meta.contextualLinksTitle}>
                    {meta.contextualLinks.map(link => (
                      <AppLink href={link.url} key={link.url} target="_blank">
                        {link.title}
                      </AppLink>
                    ))}
                  </ContextLinks>
                )}
              </SearchTitle>
            )}
            {!error && results.length > 0 && (
              <SearchFilters
                total={total}
                allFacets={facets}
                filter={filter}
                currentFilters={currentFilters}
                urlPath={urlPath}
                onFilter={handleClickSearchLink}
                selected={selected}
                showOnlySelected={showOnlySelected}
                setShowOnlySelected={setShowOnlySelected}
                open={open}
                setOpen={setOpen}
                clearFilters={handleClearFilters}
                isNewTaxonomy={true}
                selectedFilters={selectedFilters}
                params={params}
                contentRequestInProcess={contentRequestInProcess}
              />
            )}
            <div className={clsx(styles.results, error && styles.hasError)}>
              {error && <SearchError href={urlPath} />}
              {!error && (
                <SearchResults
                  title={meta.title}
                  results={resultShown}
                  selected={selected}
                  handleSelected={handleSelected}
                  setShowOnlySelected={setShowOnlySelected}
                  suggestions={suggestions}
                  handleClickSuggestion={handleClickSuggestion}
                  currentFilter={params}
                  isNewTaxonomy={true}
                  contentRequestInProcess={contentRequestInProcess}
                />
              )}
              {!error && results.length > 0 && (
                <Pagination
                  pages={pagesShown}
                  current={parseInt(params.page)}
                  generateUrl={generatePaginatedUrl}
                  onClick={handlePagination}
                />
              )}
            </div>
          </CollectionsProvider>
          <div className={styles.clear} />

          {/*todo: only render for anon users*/}
          {params.page === 1 && (
            <SearchFooter>
              <SearchFootnote
                title={meta.title}
                description={meta.description}
              />
              <SearchFootnote
                title={meta.bottomTitle}
                description={meta.bottomDescription}
              />
            </SearchFooter>
          )}
        </div>
      </div>
      <AdminLinks>
        <a href={`/secure/meta/searchpage.php?url=${urlPath}`} target="_blank">
          Edit Meta Info
        </a>
        <a href={`/secure/export-srp.php?path=${urlPath}`} target="_blank">
          Export Table
        </a>
      </AdminLinks>
    </div>
  );
}

const contextualLinkPropTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const metaPropTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  metaTitle: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  bottomTitle: PropTypes.string,
  bottomDescription: PropTypes.string,
  contextualLinksTitle: PropTypes.string,
  contextualLinks: PropTypes.arrayOf(PropTypes.shape(contextualLinkPropTypes)),
  index: PropTypes.bool,
};

const filterPropTypes = {
  q: PropTypes.string,
  page: PropTypes.number,
  sort: PropTypes.string,
  facets: PropTypes.shape({
    grades: PropTypes.arrayOf(PropTypes.string),
    contentType: PropTypes.string,
    subject: PropTypes.string,
    topic: PropTypes.string,
    standard: PropTypes.string,
    el: PropTypes.string,
    interactive: PropTypes.string,
  }),
};

const facetChildrenPropTypes = {
  urlName: PropTypes.string.isRequired,
  nesting: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
};

const facetPropTypes = {
  internalName: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(
    PropTypes.shape(facetChildrenPropTypes).isRequired
  ),
};

const resultPropTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.object,
  category: PropTypes.string,
  grade: PropTypes.string.isRequired,
  rating: PropTypes.number,
  new: PropTypes.bool,
  answersheet: PropTypes.bool.isRequired,
  premium: PropTypes.bool,
  el: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  meta: PropTypes.object,
  isInteractive: PropTypes.bool,
};

const currentFiltersPropTypes = {
  display: PropTypes.string,
  group: PropTypes.string,
  href: PropTypes.string,
};

const TaxonomyFilterAttributePropTypes = {
  name: PropTypes.string,
  url: PropTypes.string,
};

const TaxonomyFilterPropTypes = {
  name: PropTypes.string,
  url: PropTypes.string,
  attributes: PropTypes.arrayOf(
    PropTypes.shape(TaxonomyFilterAttributePropTypes)
  ),
};

const GradeFilterAttributePropTypes = {
  name: PropTypes.string,
  urlSlice: PropTypes.string,
  count: PropTypes.number,
  selected: PropTypes.bool,
  href: PropTypes.string,
  shortName: PropTypes.string,
  id: PropTypes.number,
};

const GradeFilterPropTypes = {
  name: PropTypes.string,
  href: PropTypes.string,
  anyContent: PropTypes.bool,
  attributes: PropTypes.arrayOf(PropTypes.shape(GradeFilterAttributePropTypes)),
  filterRank: PropTypes.number,
};

const ViableAttributePropTypes = {
  name: PropTypes.string,
  urlSlice: PropTypes.string,
  count: PropTypes.number,
  selected: PropTypes.bool,
  href: PropTypes.string,
};

const ViableFilterPropTypes = {
  name: PropTypes.string,
  urlSlice: PropTypes.string,
  anyContent: PropTypes.bool,
  attributes: PropTypes.arrayOf(PropTypes.shape(ViableAttributePropTypes)),
  filterRank: PropTypes.number,
};

const taxonomyCategoryPropTypes = {
  children: PropTypes.arrayOf(PropTypes.shape(this)),
  urlSlice: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  shortCategoryName: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  treeLevel: PropTypes.number,
};

const viableCategoriesPropTypes = {
  urlSlice: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  text: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape(this)),
    PropTypes.shape(this),
  ]),
};

const paramsPropTypes = {
  page: PropTypes.number,
  sort: PropTypes.string,
  q: PropTypes.string,
};

Search.propTypes = {
  total: PropTypes.number,
  pages: PropTypes.number,
  urlPath: PropTypes.string.isRequired,
  meta: PropTypes.shape(metaPropTypes).isRequired,
  filter: PropTypes.shape(filterPropTypes),
  currentFilters: PropTypes.arrayOf(PropTypes.shape(currentFiltersPropTypes)),
  facets: PropTypes.arrayOf(PropTypes.shape(facetPropTypes)),
  results: PropTypes.arrayOf(PropTypes.shape(resultPropTypes)),
  suggestions: PropTypes.arrayOf(PropTypes.string),
  replaceUrl: PropTypes.bool,
  error: PropTypes.bool,
  selectedCategory: PropTypes.shape(taxonomyCategoryPropTypes),
  viableCategories: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape(viableCategoriesPropTypes)),
    PropTypes.shape(viableCategoriesPropTypes),
  ]),
  params: PropTypes.shape(paramsPropTypes),
  selectedFilters: PropTypes.arrayOf(PropTypes.shape(TaxonomyFilterPropTypes)),
  viableGrades: PropTypes.shape(GradeFilterPropTypes),
  viableFilters: PropTypes.arrayOf(PropTypes.shape(ViableFilterPropTypes)),
  viableContentTypes: PropTypes.shape(ViableFilterPropTypes),
  hasSubjectFilters: PropTypes.bool,
};
